import { InfinityIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  bodyTypeKey,
  bodyTypesStrength,
  budgetTolerance,
  coveragePercent,
  demotedFeatureCoverage,
  demotedThresholdCoverage,
  featureKey,
  featuresStrength,
  formatCents,
  formatKilometres,
  fuelKey,
  fuelStrength,
  geographyStrength,
  hasGeography,
  hasMakes,
  hasModels,
  isUnlimitedBudget,
  makesStrength,
  maxAgeStrength,
  modelLabel,
  modelsStrength,
  orderedPriorities,
  orderedWishes,
  powertrainStrength,
  prioritiesStrength,
  priorityKey,
  stateName,
  thresholdEntries,
  usageKey,
  usageStrength,
  wishesStrength,
  type CriteriaPayload,
  type CriterionStrength,
  type Demotion,
} from '@entities/search-session';

import styles from './Criteria.module.css';

/** Ключи разделов экрана критериев (MVP_PRD §9, §11). */
export type CriterionKey =
  | 'budget'
  | 'usage'
  | 'bodyTypes'
  | 'maxAge'
  | 'annualMileage'
  | 'fuel'
  | 'transmission'
  | 'drivetrain'
  | 'priorities'
  | 'wishes'
  | 'features'
  | 'thresholds'
  | 'makes'
  | 'models'
  | 'geography';

export interface CriterionRow {
  key: CriterionKey;
  labelKey: string;
  strength: CriterionStrength;
  /** Готовая разметка значения. `null` — критерий ещё не уточняли. */
  value: ReactNode;
}

/** Порядок панели критериев (MVP_PRD §9). */
export const PANEL_ROW_ORDER: readonly CriterionKey[] = [
  'budget',
  'bodyTypes',
  'maxAge',
  'annualMileage',
  'usage',
  'drivetrain',
  'fuel',
  'transmission',
  'priorities',
  // Пожелания — сразу за приоритетами: из них приоритеты и выводятся, и читать
  // «из ваших слов» осмысленно рядом с самими словами (RFC §6.3).
  'wishes',
  'features',
  'thresholds',
  // Марки, модели и география — в конце: они не влияют на баллы (MATCHING §5.4)
  // и читаются как уточнение к запросу, а не как его основа.
  'makes',
  'models',
  'geography',
];

/**
 * Разбор Criteria Document в строки интерфейса.
 *
 * Один разбор на все места, где критерии видны: значения и метки обязаны
 * совпадать, иначе пользователь увидит два разных описания одного запроса.
 *
 * Пустая строка — это `value === null`, а не «0» и не «любой»: незаданный
 * критерий не выдаётся за решение пользователя (MVP_PRD §13).
 */
export function useCriteriaRows(
  payload: CriteriaPayload,
  demotions: readonly Demotion[] = [],
): Record<CriterionKey, CriterionRow> {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const demotedFeatures = demotedFeatureCoverage(demotions);
  const demotedThresholds = demotedThresholdCoverage(demotions);

  const list = <T extends string>(values: readonly T[], keyOf: (value: T) => string) => (
    <span>{values.map((value) => t(keyOf(value))).join(', ')}</span>
  );

  // --- Бюджет ---------------------------------------------------------------
  const { budget } = payload;
  const tolerance = budgetTolerance(budget);
  // «Бюджет не ограничен» приходит заведомо большой суммой: показывать её
  // числом нельзя — пользователь такой суммы не называл.
  const unlimited = isUnlimitedBudget(budget.listing_budget_cents);
  const budgetValue =
    budget.listing_budget_cents === null ? null : (
      <>
        {unlimited ? (
          <span className={styles.valueUnlimited}>
            <InfinityIcon size={18} weight="bold" aria-hidden="true" />
            {t('criteria.value.unlimitedBudget')}
          </span>
        ) : (
          <span className={`${styles.valueStrong} tabular`}>
            {formatCents(budget.listing_budget_cents, locale)}
          </span>
        )}
        {budget.mode && !unlimited ? (
          <span className={styles.valueNote}>{t(`criteria.budgetMode.${budget.mode}`)}</span>
        ) : null}
        {budget.reserve_cents > 0 && !unlimited ? (
          <span className={styles.valueNote}>
            {t('criteria.budget.reserve', {
              value: formatCents(budget.reserve_cents, locale),
            })}
          </span>
        ) : null}
        {tolerance ? (
          // Допуск существует только по явной фразе пользователя и показывается
          // числом: «до 30 000 €, допустимо до 32 100 €» (MVP_PRD §11).
          <span className={styles.tolerance}>
            {t('criteria.budget.tolerance', {
              base: formatCents(tolerance.baseCents, locale),
              max: formatCents(tolerance.maxCents, locale),
            })}
          </span>
        ) : null}
      </>
    );

  // --- Топливо --------------------------------------------------------------
  const { fuel } = payload;
  const fuelValue =
    fuel.allowed.length === 0 &&
    fuel.excluded.length === 0 &&
    fuel.preferred.length === 0 ? null : (
      <>
        {fuel.allowed.length > 0 ? list(fuel.allowed, fuelKey) : null}
        {fuel.excluded.length > 0 ? (
          <span className={styles.valueNote}>
            {t('criteria.fuel.excluded', {
              value: fuel.excluded.map((item) => t(fuelKey(item))).join(', '),
            })}
          </span>
        ) : null}
        {fuel.preferred.length > 0 ? (
          <span className={styles.valueNote}>
            {t('criteria.fuel.preferred', {
              value: fuel.preferred.map((item) => t(fuelKey(item))).join(', '),
            })}
          </span>
        ) : null}
      </>
    );

  const thresholds = thresholdEntries(payload.thresholds);
  const priorities = orderedPriorities(payload.priorities);
  const wishes = orderedWishes(payload.wishes);
  const { features, makes, models, geography } = payload;

  // --- Модели ---------------------------------------------------------------
  // Те же три состояния, что у марок (KARL_DIALOG_RFC §6.1): обязательная
  // модель отсеивает всё остальное, исключённая — себя, предпочтительная не
  // даёт баллов и работает только как tie-break.
  const modelsValue = !hasModels(models) ? null : (
    <>
      {models.required.length > 0 ? (
        <span className={styles.valueStrong}>
          {t('criteria.models.required', {
            value: models.required.map(modelLabel).join(', '),
          })}
        </span>
      ) : null}
      {models.excluded.length > 0 ? (
        <span className={styles.exclusion}>
          {t('criteria.models.excluded', {
            value: models.excluded.map(modelLabel).join(', '),
          })}
        </span>
      ) : null}
      {models.preferred.length > 0 ? (
        <span className={styles.valueNote}>
          {t('criteria.models.preferred', {
            value: models.preferred.map(modelLabel).join(', '),
          })}
        </span>
      ) : null}
      {models.required.length > 0 ? (
        <span className={styles.valueNote}>{t('criteria.models.requiredNote')}</span>
      ) : null}
    </>
  );

  // --- Пожелания ------------------------------------------------------------
  // Цитата обязательна и показывается: пожелание существует только вместе со
  // словами пользователя (CLAUDE.md §6.1). Сила — это вес, а не жёсткость,
  // поэтому подпись говорит «важнее», а не «обязательно».
  const wishesValue =
    wishes.length === 0 ? null : (
      <ul className={styles.inlineList}>
        {wishes.map((wish) => (
          <li key={wish.tag}>
            <span>{t(`criteria.wish.${wish.tag}`)}</span>
            {wish.strength !== 'mention' ? (
              <span className={styles.valueNote}>
                {t(`criteria.wishStrength.${wish.strength}`)}
              </span>
            ) : null}
            <span className={styles.valueQuote}>
              {t('criteria.wishes.quote', { value: wish.source_text })}
            </span>
          </li>
        ))}
      </ul>
    );

  // --- Марки ----------------------------------------------------------------
  // Три состояния и три разных смысла (MATCHING §5.4): обязательная марка
  // отсеивает всё остальное, исключённая отсеивает себя, предпочтительная не
  // даёт баллов вовсе. Показывать их одинаково значило бы соврать о выдаче.
  const makesValue = !hasMakes(makes) ? null : (
    <>
      {makes.required.length > 0 ? (
        <span className={styles.valueStrong}>
          {t('criteria.makes.required', { value: makes.required.join(', ') })}
        </span>
      ) : null}
      {makes.excluded.length > 0 ? (
        <span className={styles.exclusion}>
          {t('criteria.makes.excluded', { value: makes.excluded.join(', ') })}
        </span>
      ) : null}
      {makes.preferred.length > 0 ? (
        <span className={styles.valueNote}>
          {t('criteria.makes.preferred', { value: makes.preferred.join(', ') })}
        </span>
      ) : null}
      {makes.required.length > 0 ? (
        <span className={styles.valueNote}>{t('criteria.makes.requiredNote')}</span>
      ) : null}
    </>
  );

  // --- Где искать -----------------------------------------------------------
  // Пустой плашки «не указано» не бывает: секция скрывается целиком, если
  // пользователь о географии не говорил (FE-350).
  const geographyValue = !hasGeography(geography) ? null : (
    <>
      <span className={styles.valueStrong}>
        {geography.home_city ??
          (geography.home_state_code === null ? '' : stateName(geography.home_state_code))}
      </span>
      {geography.home_city !== null && geography.home_state_code !== null ? (
        <span className={styles.valueNote}>{stateName(geography.home_state_code)}</span>
      ) : null}
      <span className={styles.valueNote}>
        {t(
          geography.mode === 'required'
            ? 'criteria.geography.requiredNote'
            : 'criteria.geography.stepwiseNote',
        )}
      </span>
    </>
  );

  return {
    budget: {
      key: 'budget',
      labelKey: 'criteria.field.budget',
      // Бюджет — hard-критерий по определению (MATCHING §3).
      strength: budget.listing_budget_cents === null ? 'unset' : 'required',
      value: budgetValue,
    },
    usage: {
      key: 'usage',
      labelKey: 'criteria.field.usage',
      strength: usageStrength(payload),
      value:
        payload.usage.length === 0 ? null : (
          <>
            {list(payload.usage, usageKey)}
            {/* Выведено из пожеланий, а не названо прямо (RFC §6.3): человек
                сказал «семья, двое детей», сценарий сложил backend. Пользователь
                обязан это видеть — иначе он не поймёт, откуда взялась строка. */}
            {payload.usage_derived_from_wishes && !payload.usage_explicit ? (
              <span className={styles.valueNote}>{t('criteria.derivedFromWishes')}</span>
            ) : null}
          </>
        ),
    },
    bodyTypes: {
      key: 'bodyTypes',
      labelKey: 'criteria.field.bodyTypes',
      strength: bodyTypesStrength(payload),
      value:
        payload.body_types.values.length > 0 ? (
          list(payload.body_types.values, bodyTypeKey)
        ) : payload.body_types.mode === 'any' ? (
          <span className={styles.valueMuted}>{t('criteria.value.anyBody')}</span>
        ) : // Обязательная модель отвечает за кузов сама: Karl о нём не
        // спрашивает, и строка объясняет, почему она пустая (BE-546).
        payload.models.required.length > 0 && payload.models.confirmed ? (
          <span className={styles.valueMuted}>{t('criteria.value.bodyFromModel')}</span>
        ) : null,
    },
    maxAge: {
      key: 'maxAge',
      labelKey: 'criteria.field.maxAge',
      strength: maxAgeStrength(payload),
      value:
        payload.max_age.years !== null ? (
          <span className={styles.valueStrong}>
            {t('criteria.value.years', { count: payload.max_age.years })}
          </span>
        ) : payload.max_age.no_limit ? (
          <span className={styles.valueMuted}>{t('criteria.value.noLimit')}</span>
        ) : null,
    },
    annualMileage: {
      key: 'annualMileage',
      labelKey: 'criteria.field.annualMileage',
      // Годовой пробег — личное ожидание пользователя, а не фильтр (MVP_PRD §10).
      strength: payload.annual_mileage_km === null ? 'unset' : 'preferred',
      value:
        payload.annual_mileage_km === null ? null : (
          <span className="tabular">
            {t('criteria.value.kmPerYear', {
              value: formatKilometres(payload.annual_mileage_km, locale),
            })}
          </span>
        ),
    },
    fuel: {
      key: 'fuel',
      labelKey: 'criteria.field.fuel',
      strength: fuelStrength(fuel),
      value: fuelValue,
    },
    transmission: {
      key: 'transmission',
      labelKey: 'criteria.field.transmission',
      strength: powertrainStrength(payload.transmission.mode),
      value: payload.transmission.value ? (
        <span>{t(`catalog.transmission.${payload.transmission.value}`)}</span>
      ) : payload.transmission.mode === 'any' ? (
        <span className={styles.valueMuted}>{t('criteria.value.anyOption')}</span>
      ) : null,
    },
    drivetrain: {
      key: 'drivetrain',
      labelKey: 'criteria.field.drivetrain',
      strength: powertrainStrength(payload.drivetrain.mode),
      value: payload.drivetrain.value ? (
        <span>{t(`catalog.drivetrain.${payload.drivetrain.value}`)}</span>
      ) : payload.drivetrain.mode === 'any' ? (
        <span className={styles.valueMuted}>{t('criteria.value.anyOption')}</span>
      ) : null,
    },
    priorities: {
      key: 'priorities',
      labelKey: 'criteria.field.priorities',
      strength: prioritiesStrength(payload),
      value:
        priorities.length === 0 ? null : (
          <>
            {/* Ранг несёт смысл: первый приоритет весит больше остальных
                (MATCHING §7.4), поэтому список нумерованный. */}
            <ol className={styles.priorityList}>
              {priorities.map((item) => (
                <li key={item.code}>
                  <span className={`${styles.priorityRank} tabular`}>{item.rank}</span>
                  {t(priorityKey(item.code))}
                </li>
              ))}
            </ol>
            {payload.priorities_derived_from_wishes && !payload.priorities_explicit ? (
              <span className={styles.valueNote}>{t('criteria.derivedFromWishes')}</span>
            ) : null}
          </>
        ),
    },
    wishes: {
      key: 'wishes',
      labelKey: 'criteria.field.wishes',
      strength: wishesStrength(payload.wishes),
      value: wishesValue,
    },
    features: {
      key: 'features',
      labelKey: 'criteria.field.features',
      strength: featuresStrength(features),
      value:
        features.required.length === 0 && features.preferred.length === 0 ? (
          features.specified ? (
            <span className={styles.valueMuted}>{t('criteria.value.noFeatures')}</span>
          ) : null
        ) : (
          <>
            {features.required.length > 0 ? (
              <span>
                {t('criteria.features.required', {
                  value: features.required.map((item) => t(featureKey(item))).join(', '),
                })}
              </span>
            ) : null}
            {features.preferred.length > 0 ? (
              <span className={styles.valueNote}>
                {t('criteria.features.preferred', {
                  value: features.preferred.map((item) => t(featureKey(item))).join(', '),
                })}
              </span>
            ) : null}
            {/* Демоция по покрытию (RFC §6.4). Сообщается прямо и с числом:
                требование не отсеяло машины, потому что в объявлениях этого
                признака почти не указывают, и наличие спросит Karl у автохауса.
                Молчать об этом значит показывать «обязательно» рядом с
                автомобилями, о которых ничего не известно. */}
            {features.required
              .filter((item) => demotedFeatures.has(item))
              .map((item) => (
                <span key={item} className={styles.valueNote}>
                  {t('criteria.demoted.feature', {
                    value: t(featureKey(item)),
                    coverage: coveragePercent(demotedFeatures.get(item) ?? 0),
                  })}
                </span>
              ))}
          </>
        ),
    },
    makes: {
      key: 'makes',
      labelKey: 'criteria.field.makes',
      strength: makesStrength(makes),
      value: makesValue,
    },
    models: {
      key: 'models',
      labelKey: 'criteria.field.models',
      strength: modelsStrength(models),
      value: modelsValue,
    },
    geography: {
      key: 'geography',
      labelKey: 'criteria.field.geography',
      strength: geographyStrength(geography),
      value: geographyValue,
    },
    thresholds: {
      key: 'thresholds',
      labelKey: 'criteria.field.thresholds',
      // Любой заданный порог — hard-критерий: автомобиль с неизвестным
      // значением исключается (MVP_PRD §10).
      strength: thresholds.length > 0 ? 'required' : 'unset',
      value:
        thresholds.length === 0 ? null : (
          <ul className={styles.inlineList}>
            {thresholds.map((entry) => (
              <li key={entry.key} className="tabular">
                {t(`criteria.threshold.${entry.key}`, {
                  value:
                    entry.key === 'mileage_km_max'
                      ? formatKilometres(entry.value, locale)
                      : entry.value,
                  count: entry.value,
                })}
                {demotedThresholds.has(entry.key) ? (
                  <span className={styles.valueNote}>
                    {t('criteria.demoted.threshold', {
                      coverage: coveragePercent(demotedThresholds.get(entry.key) ?? 0),
                    })}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ),
    },
  };
}
